/**
 * Contacts — native only (no web equivalent the browser will hand over).
 *
 * Apple is sensitive about contacts access; the Info.plist
 * `NSContactsUsageDescription` must clearly justify the purpose, and the App
 * Store review notes need to reference where in the flow we ask. Strings live
 * in `apps/mobile-native/README.md`.
 *
 * Privacy: raw emails and phone numbers NEVER leave the device. We normalise
 * each value (email → lowercased; phone → E.164) and SHA-256 it on-device,
 * then upload only the hashes. This is what App Review requires before they'll
 * approve contacts access. We also no longer read the contact's display name.
 */

import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { isNative } from '../runtime/index.ts';

export interface ContactRecord {
  /** Native contact id — stable per device, NOT shared across devices. */
  id: string;
  /** SHA-256 hex of each normalised (lowercased) email. */
  emailHashes: string[];
  /** SHA-256 hex of each E.164-normalised phone number. */
  phoneHashes: string[];
}

export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

export async function getContactsPermissionState(): Promise<PermissionState> {
  if (!isNative()) return 'unsupported';
  const { Contacts } = await import('@capacitor-community/contacts');
  const r = await Contacts.checkPermissions();
  if (r.contacts === 'granted') return 'granted';
  if (r.contacts === 'denied') return 'denied';
  return 'prompt';
}

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Default region for parsing national-format phone numbers (those without a
 * `+` country prefix). Inferred from the device locale; numbers that already
 * carry a `+` prefix ignore it. Numbers we can't resolve to E.164 are dropped.
 */
function deviceRegion(): CountryCode | undefined {
  try {
    const loc = new Intl.Locale(navigator.language);
    const region = loc.region ?? loc.maximize().region;
    return region as CountryCode | undefined;
  } catch {
    return undefined;
  }
}

/**
 * Request permission and read the user's contacts, returning only on-device
 * hashes of each contact's emails and phone numbers. Returns `null` if the
 * user denied — callers should surface a clear "you'll need to enable contacts
 * in Settings" path rather than re-prompting (iOS will silently no-op a
 * re-ask).
 */
export async function readContacts(): Promise<ContactRecord[] | null> {
  if (!isNative()) return null;
  const { Contacts } = await import('@capacitor-community/contacts');
  const perm = await Contacts.requestPermissions();
  if (perm.contacts !== 'granted') return null;

  const result = await Contacts.getContacts({
    projection: { name: false, emails: true, phones: true },
  });
  const region = deviceRegion();

  return Promise.all(
    result.contacts.map(async (c) => {
      const emails = [
        ...new Set(
          (c.emails ?? [])
            .map((e) => e.address?.trim().toLowerCase())
            .filter((v): v is string => !!v && v.includes('@')),
        ),
      ];
      const phones = [
        ...new Set(
          (c.phones ?? [])
            .map((p) => (p.number ? parsePhoneNumberFromString(p.number, region)?.number : null))
            .filter((v): v is NonNullable<typeof v> => !!v)
            .map((v) => String(v)),
        ),
      ];
      return {
        id: c.contactId,
        emailHashes: await Promise.all(emails.map(sha256Hex)),
        phoneHashes: await Promise.all(phones.map(sha256Hex)),
      };
    }),
  );
}
